import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORMS = [
  { value: 'holiday_world', label: 'Diretta / HolidayWorld' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'booking_com', label: 'Booking.com' },
  { value: 'altri', label: 'Altra piattaforma' },
];

const EMPTY = {
  property_id: '', guest_name: '', guest_email: '', guest_phone: '',
  guests_count: 2, check_in: '', check_out: '', guest_total: '', platform: 'holiday_world',
};

export default function NewBookingModal({ open, onClose, properties = [], onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [conflictError, setConflictError] = useState(null);

  useEffect(() => {
    if (open) { setForm(EMPTY); setConflictError(null); }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleSave = async () => {
    setConflictError(null);
    if (!form.property_id) { toast.error('Seleziona un immobile'); return; }
    if (!form.guest_name.trim()) { toast.error('Inserisci il nome dell\'ospite'); return; }
    if (!form.check_in || !form.check_out) { toast.error('Inserisci le date di check-in e check-out'); return; }
    if (form.check_out <= form.check_in) { toast.error('Il check-out deve essere dopo il check-in'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/api/bookings`, {
        booking_code: `HS${Date.now().toString(36).toUpperCase()}`,
        property_id: form.property_id,
        check_in: `${form.check_in}T15:00:00`,
        check_out: `${form.check_out}T10:00:00`,
        guest_name: form.guest_name.trim(),
        guest_email: form.guest_email || null,
        guest_phone: form.guest_phone || null,
        guests_count: parseInt(form.guests_count) || 1,
        guest_total: parseFloat(form.guest_total) || 0,
        platform: form.platform,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Prenotazione creata');
      onSaved?.();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Errore durante la creazione';
      if (e.response?.status === 400 && String(detail).includes('non disponibile')) {
        setConflictError(detail);
      } else {
        toast.error(String(detail).slice(0, 300));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-booking-modal">
        <DialogHeader>
          <DialogTitle>Nuova Prenotazione</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">Immobile *</Label>
            <Select value={form.property_id} onValueChange={set('property_id')}>
              <SelectTrigger data-testid="booking-property-select"><SelectValue placeholder="Seleziona immobile" /></SelectTrigger>
              <SelectContent>
                {properties.filter((p) => !p.archived).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} {p.city ? `· ${p.city}` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Check-in *</Label>
              <Input type="date" value={form.check_in} onChange={set('check_in')} data-testid="booking-checkin-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Check-out *</Label>
              <Input type="date" value={form.check_out} onChange={set('check_out')} data-testid="booking-checkout-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Nome ospite *</Label>
            <Input value={form.guest_name} onChange={set('guest_name')} data-testid="booking-guest-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Email ospite</Label>
              <Input type="email" value={form.guest_email} onChange={set('guest_email')} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Telefono ospite</Label>
              <Input value={form.guest_phone} onChange={set('guest_phone')} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-500">N. ospiti</Label>
              <Input type="number" min="1" value={form.guests_count} onChange={set('guests_count')} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Totale €</Label>
              <Input type="number" min="0" step="0.01" value={form.guest_total} onChange={set('guest_total')} data-testid="booking-total-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Provenienza</Label>
              <Select value={form.platform} onValueChange={set('platform')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {conflictError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2" data-testid="booking-conflict-error">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 whitespace-pre-line">{conflictError}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="booking-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Crea prenotazione
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
